import { RevisionListener } from '@/common/RevisionListener';
import { VITE_APP_BASENAME } from '@/config';
import { AnalysisRoutes } from '@/features/analysis/routes';
import { AuthenticatedGuard } from '@/features/authentication/_components/AuthenticatedGuard';
import { DynamicNavigate } from '@/features/authentication/_components/DynamicNavigate';
import { MasterTableResourceGuard } from '@/features/authentication/_components/MasterTableResourceGuard';
import { OrganizationIdsGuard } from '@/features/authentication/_components/OrganizationIdsGuard';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { TagResourceGuard } from '@/features/authentication/_components/TagResourceGuard';
import { TenantManagerResourceGuard } from '@/features/authentication/_components/TenantManagerResourceGuard';
import { AuthenticationRoutes } from '@/features/authentication/routes';
import { BatchRoutes } from '@/features/batch/routes';
import { PinpointProvider } from '@/features/comment/components/pinpoint';
import { CommentCollapsibleSider } from '@/features/comment/components/sider';
import { CommentRoutes } from '@/features/comment/routes';
import { CallbackRoutes } from '@/features/gima/routes';
import { PageLayout } from '@/features/layout/_components';
import { MasterTableRoutes } from '@/features/master-table/routes';
import { ProfileRoutes } from '@/features/profile/routes';
import { BulkPrintLedgerPage } from '@/features/project/projects/detail/ledger/_pages/BulkPrintLedgerPage';
import { ProjectsRoutes } from '@/features/project/routes';
import { PageLayout as PSPageLayout } from '@/features/public-site/components';
import { PublicSiteRoutes } from '@/features/public-site/routes';
import { PAGE_INFO as PS_PAGE_INFO } from '@/features/public-site/utils';
import { TagRoutes } from '@/features/tag/routes';
import { TenantManagerRoutes } from '@/features/tenant-manager/TenantManagerRoutes';
import { WorkflowCollapsibleSider } from '@/features/workflow/components/sider';
import { WorkflowRoutes } from '@/features/workflow/routes';
import { globalLoader } from '@/lib/react-router';
import { isPS } from '@/utils';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';

export const AppRoutes = () => {
  const router = createBrowserRouter(
    [
      {
        loader: globalLoader,
        HydrateFallback: () => null,
        children: [
          ...(!isPS()
            ? [
                {
                  path: '/login/*',
                  element: <AuthenticationRoutes />,
                },
                {
                  path: '/print/:id',
                  element: (
                    <AuthenticatedGuard>
                      <BulkPrintLedgerPage />
                    </AuthenticatedGuard>
                  ),
                },
                {
                  path: '/callback/*',
                  element: <CallbackRoutes />,
                },
                {
                  path: '/',
                  element: (
                    <AuthenticatedGuard>
                      <RevisionListener />
                      <PinpointProvider>
                        <PageLayout
                          rightSider={
                            <>
                              <CommentCollapsibleSider />
                              <WorkflowCollapsibleSider />
                            </>
                          }
                        />
                      </PinpointProvider>
                    </AuthenticatedGuard>
                  ),
                  children: [
                    {
                      index: true,
                      element: <DynamicNavigate />,
                    },
                    {
                      path: '/analysis/*',
                      element: (
                        <ProjectResourceGuard showDenied>
                          <AnalysisRoutes />
                        </ProjectResourceGuard>
                      ),
                    },
                    {
                      path: '/comment/*',
                      element: <CommentRoutes />,
                    },
                    {
                      path: '/workflow/*',
                      element: <WorkflowRoutes />,
                    },
                    {
                      path: '/tag/*',
                      element: (
                        <TagResourceGuard showDenied>
                          <TagRoutes />
                        </TagResourceGuard>
                      ),
                    },
                    /* NOTE:
                     ** AWS WAF blocks and returns 403 error if the endpoint starts with `admin`.
                     ** Thus, use `tenant-manager` instead.
                     **/
                    {
                      path: '/tenant-manager/*',
                      element: (
                        <TenantManagerResourceGuard showDenied>
                          <TenantManagerRoutes />
                        </TenantManagerResourceGuard>
                      ),
                    },
                    {
                      path: '/master-table/*',
                      element: (
                        <MasterTableResourceGuard showDenied>
                          <MasterTableRoutes />
                        </MasterTableResourceGuard>
                      ),
                    },
                    {
                      path: '/profile/*',
                      element: <ProfileRoutes />,
                    },
                    {
                      path: '/batch/*',
                      element: (
                        <OrganizationIdsGuard showDenied>
                          <BatchRoutes />
                        </OrganizationIdsGuard>
                      ),
                    },
                    {
                      path: '/rs/project/*',
                      element: (
                        <ProjectResourceGuard showDenied>
                          <ProjectsRoutes />
                        </ProjectResourceGuard>
                      ),
                    },
                  ],
                },
              ]
            : [
                {
                  path: '/',
                  element: <PSPageLayout />,
                  children: [
                    {
                      index: true,
                      element: <Navigate to={PS_PAGE_INFO.top.path} />,
                    },
                    {
                      path: '/*',
                      element: <PublicSiteRoutes />,
                    },
                  ],
                },
              ]),
        ],
      },
    ],
    {
      basename: VITE_APP_BASENAME,
    },
  );
  return <RouterProvider router={router} />;
};
